import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Clock, Radio, Users } from 'lucide-react'
import ProfileLink from '../../../components/ProfileLink'
import { personName } from '../utils/teamFormat'
import { formatSigned, modeIcon, modeLabel, profitClass } from '../utils/teamStats'
import { formatAgo } from '../../profile/utils/profileFormat'
import { CARD_CLASS } from '../utils/teamUi'
import { formatPrice } from '../../../utils/formatters'
import { hapticImpact } from '../../../utils/haptic'

/**
 * Одна командная игра (docs/team-stats-api.md, §4).
 *
 * `is_session: true` — сессия кейсов, апгрейдера или контракта (своей страницы
 * у неё нет, детализация — экран §5); `false` — раунд, у него в `ref` ключ уже
 * существующей страницы карточки раунда. `in_progress` — сессия ещё идёт,
 * цифры будут расти. `detailed` — режим экрана игры: показываем, кто сколько
 * поставил и получил, в списке истории эти строки не нужны.
 */
const TeamGameCard = ({ game, accent, onOpen, detailed = false }) => {
  const { t } = useTranslation()
  const Icon = modeIcon(game)
  const members = Array.isArray(game.members) ? game.members : []
  const isClickable = typeof onOpen === 'function'

  const handleClick = () => {
    if (!isClickable) return
    hapticImpact('light')
    onOpen(game)
  }

  const Tag = isClickable ? 'button' : 'div'

  return (
    <Tag
      onClick={isClickable ? handleClick : undefined}
      className={`${CARD_CLASS} relative block w-full overflow-hidden p-3.5 text-left ${isClickable ? 'transition-all active:scale-[0.99]' : ''}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${accent}1A` }}
        >
          <Icon size={17} style={{ color: accent }} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-bold text-white">{modeLabel(game, t)}</span>
            {/* Сессия закрывается через 10 минут тишины — пока идёт, суммы растут */}
            {game.in_progress && (
              <span className="flex items-center gap-1 rounded-full border border-info/40 bg-info/10 px-2 py-0.5 text-[10px] font-semibold text-info">
                <Radio size={9} />
                {t('team.stats.inProgress', 'идёт')}
              </span>
            )}
            {game.is_session && (
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                {t('team.stats.session', 'сессия')}
              </span>
            )}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-text-secondary">
            <span className="flex items-center gap-1 tabular-nums">
              <Users size={11} />
              {game.members_count ?? members.length}
            </span>
            <span className="tabular-nums">
              {t('team.stats.gamesCount', '{{count}} игр', { count: game.games_count ?? 0 })}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={11} />
              {formatAgo(game.ended_ago ?? game.started_ago, t)}
            </span>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className={`text-sm font-bold tabular-nums ${profitClass(game.profit)}`}>
            {formatSigned(game.profit ?? 0)}
          </p>
          <p className="text-[10px] tabular-nums text-text-secondary">
            {t('team.stats.betShort', 'ставка {{bet}}', { bet: formatPrice(game.bet ?? 0) })}
          </p>
        </div>

        {isClickable && <ChevronRight size={16} className="shrink-0 text-text-secondary" />}
      </div>

      {detailed && members.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-white/5 pt-3">
          {members.map((member) => {
            const name = personName(member, t)
            return (
              <div key={member.slug} className={`flex items-center gap-2.5 ${member.is_member === false ? 'opacity-60' : ''}`}>
                <ProfileLink slug={member.slug} className="shrink-0">
                  <span className="block h-7 w-7 overflow-hidden rounded-full border border-white/10 bg-surface">
                    {member.ava ? <img src={member.ava} alt={name} className="h-full w-full object-cover" /> : null}
                  </span>
                </ProfileLink>
                <div className="min-w-0 flex-1">
                  <ProfileLink slug={member.slug} className="block">
                    <span className="block truncate text-xs font-semibold text-white">{name}</span>
                  </ProfileLink>
                  <span className="block text-[10px] tabular-nums text-text-secondary">
                    {t('team.stats.memberInGame', '{{count}} игр · ставка {{bet}}', {
                      count: member.games_count ?? 0,
                      bet: formatPrice(member.bet ?? 0)
                    })}
                  </span>
                </div>
                <span className={`shrink-0 text-xs font-bold tabular-nums ${profitClass(member.profit)}`}>
                  {formatSigned(member.profit ?? 0)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </Tag>
  )
}

export default TeamGameCard
